<template>
  <div
    ref="root"
    class="rd rd--company"
    :data-w="layout"
    :data-density="density"
    :data-bar="barSticky ? 'sticky' : 'flow'"
    :style="rootStyle"
  >
    <div class="rd-main">
      <report-nav :groups="menuGroups" :current="currentKey" :label="$t('report_mobile.title')" @select="openScreen">
        <template #tools>
          <button
            type="button"
            class="ui-icon-btn ui-icon-btn--sm ui-icon-btn--plain"
            :title="`${$t('report_desktop.shortcuts')} (?)`"
            :aria-label="$t('report_desktop.shortcuts')"
            :aria-expanded="String(helpOpen)"
            @click="helpOpen = !helpOpen"
          >
            <v-icon size="18">mdi-keyboard-outline</v-icon>
          </button>
          <button
            type="button"
            class="ui-text-btn rd-nav__legacy"
            :title="$t('report_desktop.old_view')"
            @click="openLegacy"
          >
            <v-icon size="16" color="#c2410c">mdi-history</v-icon>
            <span class="rd-nav__legacy-label">{{ $t("report_desktop.old_view") }}</span>
          </button>
        </template>
      </report-nav>

      <report-filter-bar
        v-if="!screen.noBar"
        ref="bar"
        :from="from"
        :to="to"
        :presets="presets"
        :shifts="shifts"
        :show-shifts="Boolean(screen.shifts)"
        :companies="branches"
        :selected-ids="selectedCompanyIds"
        :company-mode="showCompanies && !screen.noCompanies ? 'multi' : null"
        :labels="companyLabels"
        :search="search"
        :show-search="screen.search !== false"
        :loading="loading"
        :can-print="!screen.noExport"
        :can-excel="!screen.noExport"
        @period="onPeriod"
        @update:shifts="shifts = $event"
        @companies="onCompanies"
        @update:search="search = $event"
        @refresh="reload"
        @print="print"
        @excel="excel"
      >
        <template #filters>
          <report-checklist-menu
            v-if="showBranches"
            :label="$t('branches')"
            icon="mdi-source-branch"
            size="md"
            :min="1"
            :items="branchItems"
            :value="selectedBranchIds"
            @input="onBranches"
          ></report-checklist-menu>
          <report-choice-menu
            v-if="screen.statusFilter && statusOptions.length"
            :label="$t('order_status')"
            icon="mdi-clipboard-check-outline"
            :items="statusOptions"
            :value="employeeStatus"
            @input="onEmployeeStatus"
          ></report-choice-menu>
        </template>
      </report-filter-bar>

      <div class="rd-page">
        <div class="rd-head">
          <div class="rd-head__title">
            <h1>{{ screen.title }}</h1>
            <p class="rd-head__sub">{{ headerSubtitle }}</p>
          </div>
          <div class="rd-head__right">
            <report-kpis :kpis="kpis"></report-kpis>
          </div>
        </div>

        <div v-if="branchesLoading" class="ui-card rd-card rd-loading" aria-busy="true">
          <div v-for="line in 6" :key="line" class="rd-skeleton" :style="{ height: line === 1 ? '36px' : '28px', width: `${100 - ((line * 9) % 31)}%` }"></div>
        </div>
        <report-state v-else-if="branchesFailed" kind="error" @retry="loadBranches"></report-state>
        <report-state
          v-else-if="!ready"
          icon="mdi-domain"
          :title="$t('no_companies_selected')"
        ></report-state>
        <component
          :is="screen.component"
          v-else
          :key="`${currentKey}-${reloadKey}`"
          v-bind="screenProps"
          @kpis="kpis = $event"
          @status-options="statusOptions = $event"
          @loading="loading = $event"
          @open-panel="openPanel"
          @open-screen="openScreen"
          @toast="showToast"
        ></component>
      </div>
    </div>

    <report-detail-panel
      :stack="panelStack"
      @close="closePanel"
      @back="panelBack"
      @open="openPanel"
      @toast="showToast"
    ></report-detail-panel>
    <transition name="rd-fade">
      <div v-if="panelStack.length" class="rd-scrim" @click="closePanel"></div>
    </transition>

    <report-help-popover :open="helpOpen" :shortcuts="shortcuts" @close="helpOpen = false"></report-help-popover>
    <ui-toast :toast="toast" @action="runToastAction"></ui-toast>
  </div>
</template>

<script>
import moment from "moment";
import "@/components/report/desktop/reportDesktop.scss";
import "@/components/report/desktop/screens/group/reportDesktopGroup.scss";
import "@/components/report/desktop/screens/more/reportDesktopMore.scss";
import "./desktop/screens/multi/companyDesktopMulti.scss";
import ReportNav from "@/components/report/shared/ReportNav.vue";
import ReportHelpPopover from "@/components/report/shared/ReportHelpPopover.vue";
import ReportKpis from "@/components/report/shared/ReportKpis.vue";
import ReportFilterBar from "@/components/report/desktop/ReportFilterBar.vue";
import ReportChecklistMenu from "@/components/report/desktop/ReportChecklistMenu.vue";
import ReportChoiceMenu from "@/components/report/desktop/ReportChoiceMenu.vue";
import ReportDetailPanel from "@/components/report/desktop/ReportDetailPanel.vue";
import ReportState from "@/components/report/desktop/ReportState.vue";
import hasReportShell from "@/components/report/shared/hasReportShell";
import hasCompanyBranches from "@/mixins/report/hasCompanyBranches";
import { buildPeriodPresets, formatRange, formatShortDate } from "@/components/report/shared/periods";
import { LEGACY_UI_VALUE, QUERY_KEYS } from "@/components/report/desktop/reportQuery";
import hasCompanyReportState from "./hasCompanyReportState";
import hasCompanyGroupUsers from "./hasCompanyGroupUsers";
import { buildCompanyMenu, companyVariant, defaultCompanyScreen } from "./companyReportMenu";
import { buildCompanyDesktopScreens } from "./companyReportDesktopScreens";

const ISO = "YYYY-MM-DD";

/**
 * Izveštaji firme na računaru: gornja navigacija (grupe izveštaja), zajednička traka filtera (period, firme,
 * smene, pretraga), zaglavlje sa brojkama, izveštaj i panel sa desne strane. Isti izgled i ponašanje kao
 * /izvestaj i izveštaj gost. Period, smene, firme i pretraga (stanje zajedničko sa telefonom) su u
 * hasCompanyReportState, a raspored, panel, prečice i obaveštenja u hasReportShell. Svaki izveštaj je ekran koji
 * nasleđuje stari tab, pa štampa i Excel rade isto kao do sada. Stanje ide u adresu.
 */
export default {
  name: "CompanyReportDesktop",
  mixins: [hasReportShell, hasCompanyReportState, hasCompanyGroupUsers, hasCompanyBranches],
  components: {
    ReportNav,
    ReportHelpPopover,
    ReportKpis,
    ReportFilterBar,
    ReportChecklistMenu,
    ReportChoiceMenu,
    ReportDetailPanel,
    ReportState,
  },
  data: () => ({
    pendingSearch: "",
    /** Izbor statusa porudžbe u traci: [{ value, label, count }], javlja ga ekran (screen.statusFilter). */
    statusOptions: [],
    /** Korisnik izabran u "Kreditnim korisnicima" za "Dodaj kredit" (firma "više restorana"). */
    topupUserId: null,
  }),
  computed: {
    presets() {
      return buildPeriodPresets();
    },
    /** Obična firma, firma tipa "grupa" ili stranica "Izveštaj kantina": svaka ima svoj skup izveštaja. */
    variant() {
      return this.canteen ? "canteen" : companyVariant(this.$store.state.company);
    },
    screens() {
      return buildCompanyDesktopScreens((key, params) => this.$t(key, params), this.variant);
    },
    menuGroups() {
      return buildCompanyMenu((key, params) => this.$t(key, params), this.variant).map((group) => ({
        ...group,
        items: group.items.map((key) => ({ key, ...this.screens[key] })),
      }));
    },
    menuKeys() {
      return this.menuGroups.flatMap((group) => group.items.map((item) => item.key));
    },
    currentKey() {
      const wanted = this.$route.query[QUERY_KEYS.report];
      return this.menuKeys.includes(wanted) ? wanted : defaultCompanyScreen(this.variant);
    },
    screen() {
      return this.screens[this.currentKey];
    },
    /** Firma "više restorana" sa poslovnicama: izbor poslovnice je u traci (sve izveštaje osim unosa kredita). */
    showBranches() {
      return this.isMulti && this.reportBranches.length > 1 && !this.screen.noBar;
    },
    employeeStatus() {
      return this.$store.state.report.filters.employeeStatus;
    },
    branchItems() {
      return this.reportBranches.map((branch) => ({ value: branch.id, text: this.toLatin(branch.name || "") }));
    },
    /** Podnaslov: izabrane poslovnice, kad nisu sve. */
    branchesSubtitle() {
      if (!this.showBranches || this.selectedBranchIds.length === this.reportBranches.length) {
        return "";
      }
      if (this.selectedBranchIds.length === 1) {
        const picked = this.reportBranches.find((branch) => branch.id === this.selectedBranchIds[0]);
        return this.toLatin((picked && picked.name) || "");
      }
      return `${this.$t("branches")} ${this.selectedBranchIds.length}/${this.reportBranches.length}`;
    },
    /** Radnike za izveštaj "Pregled korisnika" grupne firme učitava ljuska (hasCompanyGroupUsers). */
    feedsGroupUsers() {
      return Boolean(this.screen.usersFeed);
    },
    /** Šta ekran dobija od ljuske: smene (kad ih koristi) i radnike (kad mu ih ljuska učitava). */
    screenProps() {
      return {
        ...(this.screen.shifts ? { shifts: this.shifts } : {}),
        ...(this.screen.userPreselect ? { preselectedUserId: this.topupUserId } : {}),
        ...(this.feedsGroupUsers ? this.groupUsersProps : {}),
      };
    },
    formatLocale() {
      const config = this.getCurrentLanguageConfig();
      return config.script === "latin" && config.locale === "sr-RS" ? "sr-Latn-RS" : config.locale;
    },
    periodLong() {
      if (this.from !== this.to) {
        return formatRange(this.from, this.to);
      }
      const weekday = new Intl.DateTimeFormat(this.formatLocale, { weekday: "long" }).format(
        moment(this.from, ISO).toDate()
      );
      return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${formatShortDate(this.from, true)}`;
    },
    shiftsInline() {
      const list = [...this.shifts].sort((a, b) => a - b);
      if (list.length === 1) {
        return this.$t("report_mobile.shift_inline", { n: list[0] });
      }
      const contiguous = list.length > 2 && list[list.length - 1] - list[0] + 1 === list.length;
      return this.$t("report_mobile.shifts_inline", {
        list: contiguous ? `${list[0]}–${list[list.length - 1]}` : list.join(", "),
      });
    },
    headerSubtitle() {
      if (this.screen.hint) {
        return this.screen.hint;
      }
      const parts = [this.periodLong];
      if (this.screen.shifts && !this.allShiftsSelected) {
        parts.push(this.shiftsInline);
      }
      if (this.selectedCompanies.length) {
        parts.push(this.companiesLabel);
      }
      parts.push(this.branchesSubtitle);
      return parts.filter(Boolean).join(" · ");
    },
    urlQuery() {
      return { ...this.stateQuery, [QUERY_KEYS.report]: this.currentKey };
    },
  },
  watch: {
    urlQuery: {
      handler(query) {
        this.writeUrl(query);
      },
      deep: true,
    },
    currentKey(key, previous) {
      if (key === previous) {
        return;
      }
      this.kpis = [];
      this.statusOptions = [];
      this.loading = false;
      this.closePanel();
      this.search = this.pendingSearch;
      this.pendingSearch = "";
      window.scrollTo(0, 0);
    },
  },
  mounted() {
    this.writeUrl(this.urlQuery);
  },
  methods: {
    onPeriod({ from, to }) {
      if (from === this.from && to === this.to) {
        return;
      }
      this.closePanel();
      this.setPeriod({ from, to });
    },
    onCompanies(ids) {
      this.closePanel();
      this.setCompanies(ids);
    },
    onEmployeeStatus(value) {
      this.$store.commit("report/SET_FILTER", { key: "employeeStatus", value });
    },
    /** Prazan izbor u store-u znači sve poslovnice. */
    onBranches(ids) {
      this.closePanel();
      this.$store.commit("report/SET_SELECTED_BRANCHES", ids.length === this.reportBranches.length ? [] : ids);
    },
    shiftDay(step) {
      const move = (date) => moment(date, ISO).add(step, "day").format(ISO);
      this.onPeriod({ from: move(this.from), to: move(this.to) });
    },
    openScreen(key, { search = "", userId = null } = {}) {
      if (!this.menuKeys.includes(key)) {
        return;
      }
      if (this.screens[key] && this.screens[key].userPreselect) {
        this.topupUserId = userId;
      }
      if (key === this.currentKey) {
        this.search = search;
        return;
      }
      this.pendingSearch = search;
      this.$router.push({ query: { ...this.$route.query, [QUERY_KEYS.report]: key } }).catch(() => {});
    },
    print() {
      this.$store.commit("SET_PRINTING");
    },
    excel() {
      this.$store.commit("SET_EXCEL");
    },
    reload() {
      this.closePanel();
      this.reloadKey += 1;
      if (this.feedsGroupUsers) {
        this.loadGroupUsers();
      }
    },
    /** Stara stranica (tabovi): ?ui=stari, dok se novi izgled ne potvrdi. */
    openLegacy() {
      this.$router.replace({ query: { ...this.$route.query, [QUERY_KEYS.legacy]: LEGACY_UI_VALUE } }).catch(() => {});
    },
  },
};
</script>
